import { accentScope } from "@ui/lib/accent-scope";
import { Button } from "@ui/primitives/Button";
import { composeRenderProps } from "@ui/primitives/shared";
import type { ReactNode } from "react";
import { TILE_DEFAULT_COLOR, type TileVariants, tile } from "./styles";

export interface TileProps {
  children?: ReactNode;
  className?: string;
  description?: string;
  /** Rich content below the label/description — e.g. an image preview or
   *  formatted value. `children` renders above the label (illustrations). */
  footer?: ReactNode;
  hierarchy?: TileVariants["hierarchy"];
  href?: string;
  isActive?: boolean;
  /** Marks the navigation destination the user is on (aria-current="page"),
   *  as opposed to `isActive`, which marks a chosen value. */
  isCurrent?: boolean;
  label: string;
  onPress?: () => void;
  shape?: TileVariants["shape"];
  size?: TileVariants["size"];
}

export function Tile({
  children,
  className,
  description,
  footer,
  hierarchy = "raised",
  href,
  isActive = false,
  isCurrent = false,
  label,
  onPress,
  shape = "rectangular",
  size,
}: TileProps) {
  const styles = tile({ hierarchy, shape, size });

  return (
    <Button
      {...accentScope(TILE_DEFAULT_COLOR)}
      aria-current={isCurrent ? "page" : undefined}
      href={href}
      onPress={onPress}
      className={composeRenderProps(className, (cn, { isHovered, isFocusVisible }) => {
        const state = isCurrent ? "current" : isActive ? "selected" : isHovered ? "hover" : "default";
        return tile({ hierarchy, shape, state, size, isFocusVisible }).root({ className: cn });
      })}
    >
      {children}
      <span className={styles.label()}>{label}</span>
      {description && <p className={styles.description()}>{description}</p>}
      {footer}
    </Button>
  );
}
